import React, { useEffect, useState, useCallback } from 'react';
import {
    Alert,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Divider,
    Stack,
    TextField,
    Typography,
    Snackbar,
    Slide,
    IconButton,
    Fab,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fade,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Gauge, gaugeClasses } from '@mui/x-charts';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import DeleteIcon from '@mui/icons-material/Delete';

import { Visibility, VisibilityOff } from '@mui/icons-material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

import './share.css';
import './Datasets.css';
import Navbar from '../components/Navbar';

const apiAdress = 'https://smartbot-api.fealinx.com'; // Change this to the IP of the API server

const Datasets = () => {
    const [question, setQuestion] = useState('');
    const [answers, setAnswers] = useState(['']);
    const [dataset, setDataset] = useState([]);
    const [datePosted, setDatePosted] = useState([]);
    const [message, setMessage] = useState({ type: '', content: '' });
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [expandedQA, setExpandedQA] = useState(null);
    const [authToken, setToken] = useState('');

    const [isLoginOpen, setLoginOpen] = useState(true);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const updateQA = useCallback((token) => {
        fetch(`${apiAdress}/datasets/`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token || authToken}`,
                'Accept': 'application/json',
            },
        })
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401) {
                        setLoginOpen(true);
                        throw new Error("401 - Non autorisé");
                    }

                    let message = "";
                    if (response.statusText) {
                        message = response.status + " - " + response.statusText;
                    } else {
                        message = response.status;
                    }
                    throw new Error(message);
                }
                return response.json();
            })
            .then((data) => {
                setDataset(data);

                if (data.length === 0) {
                    setDatePosted([]);
                    return;
                }

                // Get the date of every qa
                const dataSort = data.sort((a, b) => Date.parse(a.datetime) - Date.parse(b.datetime));
                const date = {};
                dataSort.forEach((qa) => {
                    const datetime = new Date(qa.datetime);
                    const day = datetime.toLocaleString('default', { day: 'numeric', month: 'short', year: 'numeric' });
                    if (date[day] === undefined) {
                        date[day] = 1;
                    } else {
                        date[day] += 1;
                    }
                });
                const listDate = [];
                for (const [key, value] of Object.entries(date)) {
                    listDate.push({ day: key, Total: value });
                }
                setDatePosted(listDate);
            })
            .catch((error) => {
                handleOpenSnackbar('error', "Erreur lors de la récupération des questions-réponses : " + error.message);
            });
    }, [authToken]);

    useEffect(() => {
        const token = sessionStorage.getItem('authToken');
        if (token) {
            setToken(token);
            setLoginOpen(false);
            updateQA(token);
        } else {
            setLoginOpen(true);
        }
    }, [updateQA]);

    const handleLogin = () => {
        if (username === '' || password === '') {
            handleOpenSnackbar('error', 'Veuillez remplir tous les champs');
            return;
        }

        setLoading(true);

        fetch(`${apiAdress}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
        })
            .then((response) => {
                if (!response.ok) {
                    let message = "";
                    if (response.statusText) {
                        message = response.status + " - " + response.statusText;
                    } else {
                        message = response.status;
                    }
                    throw new Error(message);
                }
                return response.json();
            })
            .then((data) => {
                setToken(data.access_token);

                // Sauvegarde du token dans le session storage
                sessionStorage.setItem('authToken', data.access_token);

                updateQA(data.access_token);
                setLoginOpen(false);
                handleOpenSnackbar('success', 'Login successful');
            })
            .catch((error) => {
                handleOpenSnackbar('error', "Erreur lors de la connexion : " + error.message);
            })
            .finally(() => setLoading(false));
    };

    const deleteQA = (id) => {
        fetch(`${apiAdress}/datasets/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'accept': '*/*',
            },
        })
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401) {
                        setLoginOpen(true);
                        throw new Error("401 - Non autorisé");
                    }

                    let message = "";
                    if (response.statusText) {
                        message = response.status + " - " + response.statusText;
                    } else {
                        message = response.status;
                    }
                    throw new Error(message);
                }
                return "Question deleted";
            })
            .then(() => {
                handleOpenSnackbar('success', 'Question supprimée');
                updateQA();
            })
            .catch((error) => {
                handleOpenSnackbar('error', "Erreur lors de la suppression de la question : " + error.message);
            });
    }

    const addQA = () => {
        if (question === '' || answers.length < 1 || answers.some((answer) => answer === '')) {
            handleOpenSnackbar('error', 'Veuillez remplir tous les champs');
            return;
        }

        fetch(`${apiAdress}/datasets/`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
                'accept': '*/*',
            },
            body: JSON.stringify({
                question: question,
                answers: answers,
            }),
        })
            .then((response) => {
                if (!response.ok) {
                    if (response.status === 401) {
                        setLoginOpen(true);
                        throw new Error("401 - Non autorisé");
                    }

                    let message = "";
                    if (response.statusText) {
                        message = response.status + " - " + response.statusText;
                    } else {
                        message = response.status;
                    }
                    throw new Error(message);
                }
                return response.json();
            })
            .then(() => {
                setQuestion('');
                setAnswers(['']);
                handleOpenSnackbar('success', 'Question ajoutée');
                updateQA();
            })
            .catch((error) => {
                handleOpenSnackbar('error', "Erreur lors de l'ajout de la question : " + error.message);
            });
    }

    const handleOpenSnackbar = (type, content) => {
        setMessage({ type, content });
        setOpenSnackbar(true);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const handleToggle = (id) => {
        setExpandedQA(expandedQA === id ? null : id);
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    return (
        <Stack className="background" spacing={2} orientation="vertical">
            <Dialog open={isLoginOpen} disableEscapeKeyDown TransitionComponent={Fade} fullScreen={fullScreen}>
                <DialogTitle style={{ textAlign: 'center' }} variant="h4" >Bienvenue</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ marginTop: '5px' }} >
                        <TextField
                            label="Nom d'utilisateur"
                            fullWidth
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                        <TextField
                            label="Mot de passe"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={togglePasswordVisibility}>
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleLogin}
                        variant="contained"
                        disabled={loading}
                        style={{
                            backgroundColor: 'var(--LOGO-FEALINX-2)',
                            color: 'white',
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Connexion'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Navbar title="Création de dataset" />
            <Stack className="container" spacing={2} direction="column" alignItems="stretch" justifyContent="flex-start">
                <Card className="card add-qa">
                    <CardHeader title="Ajout de questions-réponses" />
                    <Divider />
                    <CardContent>
                        <Stack spacing={2} direction="column">
                            <TextField label="Question" variant="outlined" value={question} onChange={(e) => setQuestion(e.target.value)} />
                            <Stack spacing={2} direction="column">
                                {answers.map((answer, index) => (
                                    <Stack key={index} direction="row" alignItems="center" spacing={1}>
                                        <TextField
                                            label={`Réponse ${index + 1}`}
                                            variant="outlined"
                                            value={answer}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            className="resizable-textfield"
                                            onChange={(e) => {
                                                const newAnswers = [...answers];
                                                newAnswers[index] = e.target.value;
                                                setAnswers(newAnswers);
                                            }} />
                                        <IconButton disabled={answers.length <= 1}
                                            onClick={() => {
                                                const newAnswers = [...answers];
                                                newAnswers.splice(index, 1);
                                                setAnswers(newAnswers);
                                            }}><RemoveIcon /></IconButton>
                                    </Stack>
                                ))}
                                <Button variant='outlined' disabled={answers.length >= 4} className='button-green border-green'
                                    onClick={() => setAnswers([...answers, ''])}>
                                    <AddIcon /> Ajouter une réponse
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                    <CardActions>
                        <Button onClick={addQA} className='button-green-cont' variant='contained'>Ajouter au dataset</Button>
                    </CardActions>
                </Card>

                <Card className="card show-qa">
                    <CardHeader title="Questions-réponses" />
                    <Divider />
                    <CardContent>
                        <Stack spacing={2} direction="column" alignItems="stretch" justifyContent="flex-start" className="qa-list">
                            {dataset.map((qa, index) => (
                                <Stack key={index} direction="row" alignItems="center" justifyContent="space-between" className="qa-container">
                                    <Stack direction="column" spacing={1}>
                                        <Typography variant="h6">{qa.question}</Typography>
                                        <Typography
                                            id={`answer-${qa.id}`}
                                            className='answer'
                                            variant="subtitle1"
                                            style={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {qa.answer.length > 350 && expandedQA !== qa.id ? qa.answer.substring(0, 300) + '...' : qa.answer}
                                        </Typography>
                                        {
                                            qa.answer.length > 350 &&
                                            <Box display="flex" justifyContent="center" alignItems="center" mt={1}>
                                                <Fab size="small" aria-label="toggle" onClick={() => handleToggle(qa.id)} 
                                                    className="button-green-cont">
                                                    {expandedQA === qa.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </Fab>
                                            </Box>
                                        }
                                    </Stack>
                                    <IconButton onClick={() => deleteQA(qa.id)} className="button-green">
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                            ))}
                            {dataset.length === 0 && <Alert severity="info">Aucune question-réponse</Alert>}
                        </Stack>
                    </CardContent>
                </Card>

                <Stack direction={{ sm: 'column', md: 'row' }} spacing={2} alignItems="stretch" justifyContent="flex-start">
                    <Card className="card stats">
                        <CardHeader title="Statistiques" />
                        <Divider />
                        <CardContent>
                            {datePosted.length > 0 ? (
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={datePosted}>
                                        <Line type="monotone" dataKey="Total" stroke="#8884d8" />
                                        <CartesianGrid stroke="#ccc" />
                                        <XAxis dataKey="day" />
                                        <YAxis />
                                        <Tooltip />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <Alert severity="info">Aucune statistique disponible</Alert>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="card progress">
                        <CardHeader title="Progression" />
                        <Divider />
                        <CardContent>
                            <Gauge
                                value={dataset.length}
                                valueMax={20}
                                innerRadius="80%"
                                outerRadius="100%"
                                height={200}
                                width={200}
                                endAngle={360}
                                cornerRadius="50%"
                                sx={(theme) => ({
                                    [`& .${gaugeClasses.valueText}`]: {
                                        fontSize: 40,
                                    },
                                    [`& .${gaugeClasses.valueArc}`]: {
                                        fill: 'var(--LOGO-FEALINX-2)',
                                    },
                                    [`& .${gaugeClasses.referenceArc}`]: {
                                        fill: 'var(--LOGO-FEALINX-4)',
                                    },
                                })}
                            />
                        </CardContent>
                    </Card>
                </Stack>

            </Stack>

            {/* Alert Section */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                TransitionComponent={(props) => <Slide {...props} direction="up" />}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={message.type}
                    sx={{ width: '100%' }}
                >
                    {message.content}
                </Alert>
            </Snackbar>
        </Stack >
    );
};

export default Datasets;
